import React, { useState, useContext, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { updatePassword } from 'aws-amplify/auth';
import { Button, Form, Breadcrumb } from 'react-bootstrap';
import { ProductContext } from '../../ProductProvider';
import Preloader from '../../components/Preloader/Preloader';
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import PasswordStrengthIndicator from "../../components/passwordStrengthIndicator";

const isNumberRegx = /\d/;
const specialCharacterRegx = /[ !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
const capsRegx = /[A-Z ]+/;

export default function ChangePassword() {
    const { loggedUser, validateLogin } = useContext(ProductContext);
    const { enqueueSnackbar } = useSnackbar();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [loaded, setLoaded] = useState(true);
    const [loading, setLoading] = useState(false);
    const [newPassFocused, setNewPassFocused] = useState(false);
    const [newPassValidity, setNewPassValidity] = useState({
      minChar: null,
      number: null,
      specialChar: null,
      capsChar: null
    });

    useEffect(() => {
        if (!loggedUser.isAuthenticated) {
          validateLogin();
        }
    }, [loggedUser]);

    const onNewChangePassword = (pass) => {
      setNewPassword(pass);
      setNewPassValidity({
        minChar: pass.length >= 8,
        number: isNumberRegx.test(pass),
        specialChar: specialCharacterRegx.test(pass),
        capsChar: capsRegx.test(pass)
      });
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();
        try {
            if (!oldPassword) {
                enqueueSnackbar("Please enter your current password", { variant: 'info' });
                return;
            }
            if (!newPassword) {
                enqueueSnackbar("Please enter your new password", { variant: 'info' });
                return;
            }
            if (newPassword !== confirmPass) {
                enqueueSnackbar("New Password and Confirm Password do not match", { variant: 'warning' });
                return;
            }
            setLoading(true);
            setLoaded(false);

            await updatePassword({ oldPassword, newPassword });
            enqueueSnackbar("Password Updated Successfully! Your account is now secured.", { variant: 'success' });
            setOldPassword('');
            setNewPassword('');
            setConfirmPass('');
            setLoaded(true);
            setLoading(false);
        } catch (err) {
            setLoaded(true);
            setLoading(false);
            enqueueSnackbar("Error updating password: " + err.message, { variant: 'warning' });
        }
    };

  return (
    <div className="change-password-page animate-fade-in-up">
      <Helmet>
        <title>Account Security & Change Password - Vidyajyoti College</title>
        <meta name="description" content="Update your student portal password and account security settings" />
        <link rel="canonical" href="/change-password" />
      </Helmet>

      <Preloader show={!loaded} />

      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/">
            <i className="fa fa-home" aria-hidden="true"></i> Dashboard
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Profile</Breadcrumb.Item>
        <Breadcrumb.Item active>Change Password</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <div className="page-title-section">
        <div className="page-title-wrapper">
          <h4>
            <i className="fa fa-lock text-primary"></i> Account Security & Password
          </h4>
          <div className="page-subtitle">
            Manage your student credentials and secure authentication
          </div>
        </div>
      </div>

      <div className="row justify-content-center mt-3">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="card p-4 p-md-5 shadow-sm">
            <div className="text-center mb-4">
              <div 
                className="action-btn-circle mx-auto mb-2" 
                style={{ width: '54px', height: '54px', background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }}
              >
                <i className="fa fa-shield fa-2x"></i>
              </div>
              <h5 className="fw-bold mb-1" style={{ color: 'var(--txt-color)' }}>Change Your Password</h5>
              <p className="text-muted small">Choose a strong password with at least 8 characters</p>
            </div>

            <Form onSubmit={handleChangePassword}>
              <Form.Group className="mb-3" controlId="userEmail">
                <Form.Label className="fw-semibold small" style={{ color: 'var(--txt-color)' }}>
                  Student Account
                </Form.Label>
                <Form.Control 
                  type="email" 
                  value={loggedUser.email || loggedUser.name || 'student@vidyajyoticollege.in'}
                  disabled
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="currentPassword">
                <Form.Label className="fw-semibold small" style={{ color: 'var(--txt-color)' }}>
                  Current Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control 
                    type={showOld ? "text" : "password"} 
                    placeholder="Enter your current password"               
                    onChange={e => setOldPassword(e.target.value)}
                    value={oldPassword}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className="btn position-absolute text-muted p-0"
                    onClick={() => setShowOld(!showOld)}
                    style={{ top: '50%', right: '14px', transform: 'translateY(-50%)', background: 'none', border: 'none' }}
                  >
                    <i className={`fa ${showOld ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </Form.Group>

              <Form.Group className="mb-3" controlId="newPassword">
                <Form.Label className="fw-semibold small" style={{ color: 'var(--txt-color)' }}>
                  New Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control 
                    type={showNew ? "text" : "password"} 
                    placeholder="Enter new strong password"               
                    onFocus={() => setNewPassFocused(true)}
                    onChange={e => onNewChangePassword(e.target.value)}
                    value={newPassword}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    className="btn position-absolute text-muted p-0"
                    onClick={() => setShowNew(!showNew)}
                    style={{ top: '50%', right: '14px', transform: 'translateY(-50%)', background: 'none', border: 'none' }}
                  >
                    <i className={`fa ${showNew ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </Form.Group>

              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label className="fw-semibold small" style={{ color: 'var(--txt-color)' }}>
                  Confirm New Password
                </Form.Label>
                <Form.Control 
                  type="password" 
                  placeholder="Re-enter new password"               
                  onChange={e => setConfirmPass(e.target.value)}
                  value={confirmPass}
                  disabled={loading}
                  required
                />
              </Form.Group>

              {newPassFocused && (
                <div className="p-3 mb-3 rounded-3" style={{ background: 'var(--input-bg, #f8fafc)', border: '1px solid var(--card-border, #e2e8f0)' }}>
                  <PasswordStrengthIndicator validity={newPassValidity} />
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-100 mt-2 fw-bold shadow"
                disabled={loading}
                style={{ borderRadius: '12px' }}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i> Updating Password...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check me-2"></i> Update Password
                  </>
                )}
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
